import { Injectable, Injector, OnDestroy } from '@angular/core';
import { IMAGE_EXTENSIONS } from '../models/constants';
import { AttachmentService } from './attachment.service';
import { ImageService } from './image.service';
import { DecodedQRCode } from '../models/qr-code.model';
import { EvaluationFlowsEnum, IEvaluationStepsConfig } from '../models/shared.model';
import { IEvaluator } from '../interfaces/evaluator-interface';
import { Evaluator } from '../evaluators/evaluator';
import { AnalysisRun, AnalysisRunService } from './analysis-run.service';
import { ANALYSIS_TIMEOUTS, bounded, AnalysisError } from '../utils/analysis-async';

@Injectable()
export class QRCodeService implements OnDestroy {
  private requestSequence = 0;
  private urls: string[] = [];
  private urlRunId: number;
  constructor(private injector: Injector, private attachmentService: AttachmentService,
              private imageService: ImageService, private runs: AnalysisRunService) {}

  public async qrCodeFlow(enumValue: string, flow: IEvaluationStepsConfig, run: AnalysisRun = this.runs.current) {
    const images = await this.getQrCodes(run);
    const evaluators: IEvaluator[] = [];
    for (let index = 0; index < images.length; index++) {
      run.assertActive();
      try {
        const decoded = await this.decodeQRCode(images[index], run);
        run.assertActive();
        if (decoded) evaluators.push(new Evaluator(this.injector, enumValue, flow, undefined, undefined, undefined, decoded));
      } catch (error) {
        run.assertActive();
        run.issue(EvaluationFlowsEnum.QRCODE, error, `decode:${index}`);
      }
    }
    return evaluators;
  }

  async getQrCodes(run: AnalysisRun = this.runs.current): Promise<Blob[]> {
    run.assertActive();
    const collections = await Promise.allSettled([
      this.attachmentService.loadImagesFromAttachments(IMAGE_EXTENSIONS, run),
      this.imageService.loadImageTags(run)
    ]);
    run.assertActive();
    const images: Blob[] = [];
    collections.forEach((result, index) => {
      if (result.status === 'fulfilled') images.push(...result.value);
      else run.issue(EvaluationFlowsEnum.QRCODE, result.reason, index === 0 ? 'attachment-images' : 'body-images');
    });
    return images;
  }

  protected createWorker(): Worker {
    return new Worker(new URL('../../workers/qr-worker.worker', import.meta.url), { type: 'module' });
  }

  async decodeQRCode(blob: Blob, run: AnalysisRun = this.runs.current): Promise<DecodedQRCode | null> {
    run.assertActive();
    let expired = false;
    let bitmap: ImageBitmap | undefined;
    let worker: Worker | undefined;
    let handleMessage: (event: MessageEvent) => void;
    let handleError: (event: ErrorEvent) => void;
    const requestId = ++this.requestSequence;
    try {
      return await bounded(async () => {
        const decodedBitmap = await createImageBitmap(blob);
        if (expired || !run.active) { decodedBitmap.close(); throw new AnalysisError('cancelled', 'Image decode cancelled'); }
        bitmap = decodedBitmap;
        run.assertActive();
        worker = this.createWorker();
        const data = await new Promise<string | null>((resolve, reject) => {
          handleMessage = event => {
            if (event.data.runId !== run.id || event.data.requestId !== requestId) return;
            if (!event.data.success) reject(new AnalysisError('failed', event.data.error || 'QR worker failed'));
            else resolve(event.data.data || null);
          };
          handleError = event => reject(event.error || new AnalysisError('failed', 'QR worker failed'));
          worker.addEventListener('message', handleMessage);
          worker.addEventListener('error', handleError);
          worker.postMessage({ bitmap, runId: run.id, requestId }, [bitmap]);
        });
        run.assertActive();
        if (!data) return null;
        if (this.urlRunId !== run.id) { this.releaseUrls(); this.urlRunId = run.id; }
        const url = URL.createObjectURL(blob);
        this.urls.push(url);
        return { qr_code: url, decoded: data, isHovering: false };
      }, ANALYSIS_TIMEOUTS.decode, 'QR decode', run);
    } finally {
      expired = true;
      if (worker) {
        worker.removeEventListener('message', handleMessage);
        worker.removeEventListener('error', handleError);
        worker.terminate();
      }
      bitmap?.close();
    }
  }
  private releaseUrls(): void { this.urls.splice(0).forEach(url => URL.revokeObjectURL(url)); }
  ngOnDestroy(): void { this.releaseUrls(); }
}
